import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"
import { sdk } from "@/api/sdk"
import { queryClient } from "./client"
import type {
  EditLoanMutationVariables,
  PreQualificationMutationInput,
} from "@/generated/graphql"
import { loanDashboardKeys } from "@/queries/loan-dashboard"

export const useLoans = (ordering: string, filter: string | null) => {
  return useInfiniteQuery({
    queryKey: ["loans", ordering, filter],
    queryFn: async ({ pageParam }) => {
      try {
        return await sdk.Loans({ ordering, page: pageParam, filter })
      } catch (err) {
        console.error(err)
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!lastPage || lastPage.loanApplications?.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
  })
}

export const useAddLoan = () => {
  return useMutation({
    mutationFn: async (addPayload: {
      placeId?: string
      analysisId?: string
    }) => {
      try {
        return await sdk.AddLoan(addPayload)
      } catch (err) {
        console.error(err)
      }
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["loans"] })
      await queryClient.invalidateQueries({
        queryKey: loanDashboardKeys.lists(),
      })
    },
  })
}

export const useEditLoan = () => {
  return useMutation({
    mutationFn: async (editPayload: EditLoanMutationVariables) => {
      /* console.log(
        "Sending EditLoan mutation with:",
        JSON.stringify(editPayload, null, 2)
      ) */
      return await sdk.EditLoan(editPayload)
    },
    onSuccess: async (data) => {
      console.log(
        "EditLoan mutation successful:",
        JSON.stringify(data, null, 2)
      )
      await queryClient.invalidateQueries({ queryKey: ["loans"] })
      await queryClient.invalidateQueries({
        queryKey: ["loan", data.updateLoanApplication?.loanApplication?.id],
      })
      await queryClient.invalidateQueries({ queryKey: ["user"] })
      await queryClient.invalidateQueries({
        queryKey: [
          "combinedLoanData",
          data.updateLoanApplication?.loanApplication?.id,
        ],
      })
    },
  })
}

export const useCombinedLoanData = (loanId?: string, version?: number) => {
  return useQuery({
    queryKey: ["combinedLoanData", loanId, version],
    queryFn: async () => {
      try {
        if (!loanId) {
          return null
        }
        const response = await sdk.CombinedLoanAndProducts({
          loanId,
          version: version ?? 2,
        }) // Default version to 2
        if (!response) {
          throw new Error("No response from CombinedLoanAndProducts query")
        }
        /* console.log(
          `*** Getting useCombinedLoanData query with: ${version}`,
          JSON.stringify(response, null, 2)
        ) */
        return response
      } catch (err) {
        console.error("Error fetching combined loan data:", err)
        throw err
      }
    },
  })
}

export const useUpsertLoanPreQual = () => {
  return useMutation({
    mutationFn: async (upsertPayload: {
      input: PreQualificationMutationInput
    }) => {
      try {
        return await sdk.UpsertLoanPreQual(upsertPayload)
      } catch (err) {
        console.error(err)
      }
    },
  })
}

export const useCreditPull = () => {
  return useMutation({
    mutationFn: async (payload: {
      loanId: string
      phoneNumber: string
      placeId: string
      firstName: string
      lastName: string
      dateOfBirth: string
    }) => {
      try {
        const ret = await sdk.SoftCreditPullMutation(payload)
        if (ret.creditReportMutation?.result?.error) {
          throw new Error(ret.creditReportMutation.result.error)
        }
        return true
      } catch (err) {
        console.error(err)
        throw err
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user"] })
    },
  })
}

export const useCreatePreApprovalRequest = () => {
  return useMutation({
    mutationFn: async ({
      phoneNumber,
      propertyId,
    }: {
      phoneNumber: string
      propertyId?: string | null
    }) => {
      try {
        /* console.log("Sending CreatePreApprovalRequest mutation with:", {
          phoneNumber,
          propertyId,
        }) */
        // Directly pass phoneNumber and propertyId to the mutation
        return await sdk.AddPreApprovalRequest({ phoneNumber, propertyId })
      } catch (err) {
        console.error("Error during Pre-Approval Request:", err)
        throw err // Rethrow the error to ensure it gets handled by onError or further up in the component tree
      }
    },
    onSuccess: async (data) => {
      // console.log("Pre-Approval Request Successful:", data)
      await queryClient.invalidateQueries({ queryKey: ["preApprovalRequests"] })
    },
    onError: (error: Error) => {
      console.error("Error during Pre-Approval Request:", error)
    },
  })
}
